import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, Check, Truck, Wallet as WalletIcon, HeartHandshake, Stethoscope, Award } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const PERK_ICONS = [Truck, WalletIcon, HeartHandshake, Stethoscope, Award];

const Elite = () => {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [sp] = useSearchParams();
    const [plans, setPlans] = useState([]);
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/elite/plans").then((r) => setPlans(r.data.items || [])),
            api.get("/elite/me").then((r) => setMe(r.data)).catch(() => setMe(null)),
        ]).finally(() => setLoading(false));
        if (sp.get("activated") === "1") {
            toast.success("Welcome to PILLS Elite ✨");
            refresh?.();
        }
    }, [sp, refresh]);

    const subscribe = async (slug) => {
        setBusy(slug);
        try {
            const { data } = await api.post("/elite/subscribe", { plan_slug: slug });
            if (data.amount === 0) {
                toast.success("Elite activated");
                refresh?.();
                navigate("/elite?activated=1");
            } else {
                window.location.href = data.redirect_url;
            }
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not start checkout");
        } finally {
            setBusy(null);
        }
    };

    const monthly = useMemo(() => plans.find((p) => p.slug === "monthly"), [plans]);
    const quarterly = useMemo(() => plans.find((p) => p.slug === "quarterly"), [plans]);
    const yearly = useMemo(() => plans.find((p) => p.slug === "yearly") || plans.find((p) => p.slug === "annual"), [plans]);

    // Savings vs monthly extrapolation
    const savingsByPlan = (p) => {
        if (!p || !monthly) return 0;
        if (p.slug === "yearly" || p.slug === "annual") return Math.max(0, monthly.price_inr * 12 - p.price_inr);
        if (p.slug === "quarterly") return Math.max(0, monthly.price_inr * 3 - p.price_inr);
        return 0;
    };

    const sortedPlans = useMemo(() => {
        const order = { monthly: 0, quarterly: 1, yearly: 2, annual: 2 };
        return [...plans].sort((a, b) => (order[a.slug] ?? 99) - (order[b.slug] ?? 99));
    }, [plans]);

    return (
        <AuthGuard>
            <div className="min-h-screen bg-gradient-to-b from-amber-50/60 via-white to-white dark:from-amber-950/20 dark:via-[#0B0B12] dark:to-[#0B0B12]">
                <Navbar />
                <main className="mx-auto max-w-5xl px-6 py-14 lg:px-12">
                    <div className="text-center">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-amber-800">
                            <Sparkles className="h-3.5 w-3.5" /> PILLS Elite
                        </span>
                        <h1 className="mt-6 text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl lg:text-6xl dark:text-zinc-50" style={{ letterSpacing: "-0.02em" }}>
                            Premium healthcare,<br /><span className="text-amber-700">on autopilot.</span>
                        </h1>
                        <p className="mx-auto mt-4 max-w-xl text-base text-zinc-500">
                            Free shipping on every order, 2% cashback on every paid order, priority pharmacist routing, and more.
                        </p>
                    </div>

                    {me?.active && (
                        <div className="mx-auto mt-10 max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-5 text-center text-sm text-amber-900 shadow-sm" data-testid="elite-active-banner">
                            <p className="font-semibold">You're an Elite member ✨</p>
                            <p className="mt-1">Plan: {me.plan} • {me.days_remaining} day(s) remaining • Expires {new Date(me.expires_at).toLocaleDateString()}</p>
                        </div>
                    )}

                    {/* Plans */}
                    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {loading ? (
                            <div className="col-span-full text-center text-sm text-zinc-500">Loading plans…</div>
                        ) : sortedPlans.map((p) => {
                            const featured = (p.slug === "yearly" || p.slug === "annual");
                            const isQuarterly = p.slug === "quarterly";
                            const monthly_eq = featured ? Math.round(p.price_inr / 12)
                                : isQuarterly ? Math.round(p.price_inr / 3) : null;
                            const savings = savingsByPlan(p);
                            const cycleLabel = p.duration_days === 30 ? "month" : p.duration_days === 90 ? "quarter" : "year";
                            return (
                                <div
                                    key={p.slug}
                                    className={`relative rounded-3xl border p-7 shadow-sm transition-shadow hover:shadow-md ${
                                        featured ? "border-amber-300 bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/30 dark:to-zinc-900"
                                        : isQuarterly ? "border-brand-200 bg-gradient-to-br from-brand-50 to-white dark:from-brand-950/30 dark:to-zinc-900"
                                        : "border-zinc-100 bg-white dark:bg-white/[0.03]"
                                    }`}
                                    data-testid={`elite-plan-${p.slug}`}
                                >
                                    {savings > 0 && (
                                        <span className={`absolute -top-3 right-6 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white ${featured ? "bg-amber-600" : "bg-brand-700"}`}>
                                            Save ₹{savings}
                                        </span>
                                    )}
                                    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{p.display_name}</p>
                                    <div className="mt-3 flex items-baseline gap-2">
                                        <p className="text-4xl font-semibold text-zinc-900 dark:text-zinc-50" style={{ letterSpacing: "-0.02em" }}>{inr(p.price_inr)}</p>
                                        <p className="text-sm text-zinc-500">/{cycleLabel}</p>
                                    </div>
                                    {monthly_eq && <p className="mt-1 text-xs text-zinc-500">Just ₹{monthly_eq}/month, billed {cycleLabel === "year" ? "annually" : "quarterly"}</p>}
                                    <ul className="mt-5 space-y-2.5">
                                        {(p.perks || []).map((perk, i) => {
                                            const Icon = PERK_ICONS[i % PERK_ICONS.length];
                                            return (
                                                <li key={perk.label} className="flex items-start gap-2.5">
                                                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${featured ? "text-amber-700" : isQuarterly ? "text-brand-700" : "text-zinc-500"}`} />
                                                    <div>
                                                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-50">{perk.label}</p>
                                                        <p className="text-xs text-zinc-500">{perk.description}</p>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <button
                                        type="button"
                                        disabled={busy === p.slug || me?.active}
                                        onClick={() => subscribe(p.slug)}
                                        className={`mt-6 inline-flex w-full items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-medium transition-colors ${
                                            featured ? "bg-amber-700 text-white hover:bg-amber-800"
                                            : isQuarterly ? "bg-brand-700 text-white hover:bg-brand-600"
                                            : "border border-zinc-200 bg-white text-zinc-900 hover:bg-zinc-50 dark:bg-white/5 dark:text-zinc-50"
                                        } disabled:cursor-not-allowed disabled:opacity-50`}
                                        data-testid={`elite-subscribe-${p.slug}`}
                                    >
                                        {me?.active ? <><Check className="h-4 w-4" /> Already a member</> : busy === p.slug ? "Redirecting…" : `Subscribe for ${inr(p.price_inr)}`}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-10 text-center text-xs text-zinc-500">Cancel anytime. Membership doesn't auto-renew — you keep access until expiry.</p>
                </main>
                <Footer />
            </div>
        </AuthGuard>
    );
};

export default Elite;
